import { create } from 'zustand';
import type { Plan, Subscription } from '../types/subscription';
import type { VPNKey } from '../types/vpnkey';
import type { Trial, TrialConfig, TrialStatus } from '../types/trial';
import type { CdnPackages, CdnQuota } from '../types/cdn';
import { getPlans, getCurrentSubscription } from '../api/subscription.api';
import { getMyKeys } from '../api/vpn.api';
import { getTrialConfig, getTrialStatus } from '../api/trial.api';
import { getCdnPackages, getCdnQuota } from '../api/cdn.api';

interface SubscriptionState {
  plans: Plan[];
  currentSubscription: Subscription | null;
  keys: VPNKey[];
  trialStatus: TrialStatus | null;
  /** Параметры триала (/trial/config) — публичные, нужны до активации и на лендинге */
  trialConfig: TrialConfig | null;
  /** Квота обхода белых списков; null — данных нет (401/500) или ещё не запрашивали */
  cdnQuota: CdnQuota | null;
  /** Прайс докупки (/cdn/packages); грузится лениво — только там, где нужен */
  cdnPackages: CdnPackages | null;
  loading: boolean;
  fetchPlans: () => Promise<void>;
  fetchSubscription: () => Promise<void>;
  fetchKeys: () => Promise<void>;
  fetchTrial: () => Promise<void>;
  fetchTrialConfig: () => Promise<void>;
  fetchCdnQuota: () => Promise<void>;
  fetchCdnPackages: () => Promise<void>;
  fetchAll: () => Promise<void>;
  /** Точечно обновляет один ключ (напр. после переименования) без refetch и глобального спиннера */
  updateKey: (key: VPNKey) => void;
  /** Точечно обновляет триал (напр. после отвязки устройства) без refetch и глобального спиннера */
  updateTrial: (trial: Trial) => void;
}

export const useSubscriptionStore = create<SubscriptionState>((set) => ({
  plans: [],
  currentSubscription: null,
  keys: [],
  trialStatus: null,
  trialConfig: null,
  cdnQuota: null,
  cdnPackages: null,
  loading: false,

  fetchPlans: async () => {
    try {
      const plans = await getPlans();
      set({ plans });
    } catch {
      // Plans may fail if not logged in
    }
  },

  fetchSubscription: async () => {
    try {
      const sub = await getCurrentSubscription();
      set({ currentSubscription: sub });
    } catch {
      set({ currentSubscription: null });
    }
  },

  fetchKeys: async () => {
    try {
      const keys = await getMyKeys();
      set({ keys });
    } catch {
      set({ keys: [] });
    }
  },

  fetchTrial: async () => {
    try {
      const trialStatus = await getTrialStatus();
      set({ trialStatus });
    } catch {
      set({ trialStatus: null });
    }
  },

  fetchTrialConfig: async () => {
    try {
      const trialConfig = await getTrialConfig();
      set({ trialConfig });
    } catch {
      // Конфиг статичен — при сбое оставляем то, что уже было
    }
  },

  fetchCdnQuota: async () => {
    try {
      const cdnQuota = await getCdnQuota();
      set({ cdnQuota });
    } catch {
      // 401/500 — блок квоты просто не покажется
      set({ cdnQuota: null });
    }
  },

  fetchCdnPackages: async () => {
    try {
      const cdnPackages = await getCdnPackages();
      set({ cdnPackages });
    } catch {
      // Прайс не критичен: строка докупки и модалка покажут «недоступно»
    }
  },

  fetchAll: async () => {
    set({ loading: true });
    try {
      const [plans, sub, keys, trialStatus, cdnQuota, trialConfig] = await Promise.allSettled([
        getPlans(),
        getCurrentSubscription(),
        getMyKeys(),
        getTrialStatus(),
        getCdnQuota(),
        getTrialConfig(),
      ]);
      set((state) => ({
        plans: plans.status === 'fulfilled' ? plans.value : [],
        currentSubscription: sub.status === 'fulfilled' ? sub.value : null,
        keys: keys.status === 'fulfilled' ? keys.value : [],
        trialStatus: trialStatus.status === 'fulfilled' ? trialStatus.value : null,
        // Ошибку /cdn/quota глотаем: без данных блок не рендерится
        cdnQuota: cdnQuota.status === 'fulfilled' ? cdnQuota.value : null,
        trialConfig: trialConfig.status === 'fulfilled' ? trialConfig.value : state.trialConfig,
      }));
    } finally {
      set({ loading: false });
    }
  },

  updateKey: (key) =>
    set((state) => ({
      keys: state.keys.map((k) => (k.id === key.id ? key : k)),
    })),

  updateTrial: (trial) =>
    set((state) => ({
      trialStatus: state.trialStatus ? { ...state.trialStatus, trial } : state.trialStatus,
    })),
}));
